'use client';

import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';
import {
  getActiveVerification,
  getVerificationDisplayStatus
} from '@/components/contacts/contact-card-simplified/types';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';

// Per-field validators surfaced via Controller `rules` so BSB / account number
// errors appear inline as the user types (matching the legacy UX). All other
// validation happens at submit time in ContactFormCard's onSubmit handler.
const bsbValidator = z
  .string()
  .optional()
  .nullable()
  .refine(
    (val) => !val || val === '' || /^\d{6}$/.test(val),
    'BSB must be exactly 6 digits'
  );

const accountNumberValidator = z
  .string()
  .optional()
  .nullable()
  .refine(
    (val) => !val || val === '' || /^\d+$/.test(val),
    'Account number must contain only digits'
  )
  .refine(
    (val) => !val || val === '' || (val.length >= 6 && val.length <= 9),
    'Account number must be 6-9 digits'
  );

const bsbRule = (value: unknown) => {
  const result = bsbValidator.safeParse(value);
  return result.success ? true : result.error.issues[0]?.message;
};

const accountNumberRule = (value: unknown) => {
  const result = accountNumberValidator.safeParse(value);
  return result.success ? true : result.error.issues[0]?.message;
};

export function BankAccountSection({
  control,
  mode,
  values,
  clickToCopy = false,
  showFileNote = false
}: ContactFormSectionProps): ReactElement {
  const activeVerification = getActiveVerification(values?.verifications);
  const verificationStatus = getVerificationDisplayStatus(activeVerification);
  // Per-matter verification (LF-3696): a Triple Lock result on this matter
  // OR the matter-scoped `isBankVerified` flag both count as verified.
  // `bankAccount.isVerified` is the contact-global account fingerprint and
  // must not gate matter-scoped UI.
  const isVerified =
    verificationStatus === 'VERIFIED_HIGHEST' ||
    verificationStatus === 'VERIFIED_HIGH' ||
    values?.isBankVerified === true;

  const hasBankData = !!(
    values?.bankAccount?.accountName ||
    values?.bankAccount?.bsb ||
    values?.bankAccount?.accountNumber ||
    values?.bankAccount?.isVerified ||
    values?.bankAccount?.verificationNote
  );
  const [showBankAccount, setShowBankAccount] = useState<boolean>(hasBankData);

  const fieldChanges = useFieldChanges();
  const bankAccountChange = fieldChanges?.bankAccount;
  const isBankEnrichment = bankAccountChange?.changeType === 'enrichment';
  const isBankConflict = bankAccountChange?.changeType === 'conflict';

  if (mode === 'read') {
    if (isBankConflict) {
      return <Fragment />;
    }
    if (!hasBankData) {
      return <Fragment />;
    }

    const enrichTag = isBankEnrichment ? ('enrichment' as const) : undefined;

    return (
      <Fragment>
        {values.bankAccount?.accountName && (
          <FormField
            label="Account Name"
            value={values.bankAccount.accountName}
            mode="read"
            clickToCopy={clickToCopy}
            changeType={enrichTag}
          />
        )}
        <div className="flex items-center gap-4">
          {values.bankAccount?.bsb && (
            <FormField
              label="BSB"
              value={values.bankAccount.bsb}
              mode="read"
              clickToCopy={clickToCopy}
              changeType={enrichTag}
            />
          )}
          {values.bankAccount?.accountNumber && (
            <FormField
              label="Account Number"
              value={values.bankAccount.accountNumber}
              mode="read"
              clickToCopy={clickToCopy}
              changeType={enrichTag}
            />
          )}
        </div>
        {values.isBankVerified && (
          <FormField
            label="Verified"
            value="Yes"
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}
        {showFileNote && values.bankAccount?.verificationNote && (
          <FormField
            label="File Note"
            value={values.bankAccount.verificationNote}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}
      </Fragment>
    );
  }

  if (!showBankAccount) {
    return (
      <Button
        size="xs"
        density="compact"
        width="full"
        align="start"
        type="button"
        variant="ghost"
        onClick={() => setShowBankAccount(true)}
      >
        + Add Banking Details
      </Button>
    );
  }

  return (
    <Fragment>
      <div className="grid grid-cols-2 gap-3">
        <Controller
          control={control as Control<Partial<Contact>>}
          name="bankAccount.bsb"
          rules={{ validate: bsbRule }}
          render={({ field, fieldState }) => (
            <FormField
              label="BSB"
              value={(field.value as string) || ''}
              mode="edit"
              placeholder="Add BSB"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control as Control<Partial<Contact>>}
          name="bankAccount.accountNumber"
          rules={{ validate: accountNumberRule }}
          render={({ field, fieldState }) => (
            <FormField
              label="Account Number"
              value={(field.value as string) || ''}
              mode="edit"
              placeholder="Add account number"
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      {!isVerified && (
        <Controller
          control={control as Control<Partial<Contact>>}
          name="bankAccount.isVerified"
          render={({ field, fieldState }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="bankAccountIsVerified"
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(checked)}
              />
              <label
                htmlFor="bankAccountIsVerified"
                className={cn(tokens.patterns.form.label, 'cursor-pointer')}
              >
                Mark as Verified
              </label>
              {fieldState.error?.message && (
                <span className="text-destructive text-sm">
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />
      )}

      {showFileNote && (
        <Controller
          control={control as Control<Partial<Contact>>}
          name="bankAccount.verificationNote"
          render={({ field, fieldState }) => (
            <div className={tokens.patterns.form.spacing}>
              <label
                htmlFor="bankAccountVerificationNote"
                className={tokens.patterns.form.label}
              >
                File Note
              </label>
              <Textarea
                id="bankAccountVerificationNote"
                placeholder="Add verification notes..."
                value={(field.value as string) || ''}
                onChange={(e) => field.onChange(e.target.value)}
                rows={3}
                className={cn(
                  'min-h-[60px]',
                  tokens.patterns.form.input,
                  fieldState.error && 'border-destructive'
                )}
              />
              {fieldState.error?.message && (
                <span className="text-destructive text-sm">
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />
      )}
    </Fragment>
  );
}
